// components/personalize/AIPersonalizeModal.tsx
'use client';
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useRouter } from 'next/router';
import { v4 as uuidv4 } from 'uuid';
import toast from 'react-hot-toast';
import CustomToast from '../CustomToast';
import useAuth from '@/hooks/useAuth';
import { useLoginModal } from '@/contexts/LoginModalContext';
import { useQuery, useMutation } from 'react-query';
import { GetDocument } from '@/services/api/personalize/GetDocuments';
import CoversSelection from './CoversSelection';
import GetCovers from '@/services/api/personalize/GetCovers';
import { GetSettings } from '@/services/api/settings';
import { useCartDispatch } from '@/contexts/CartContext';
import { addItem, updateItem } from 'CartActions';
import { event } from 'nextjs-google-analytics';
import * as fbq from '@/services/fbpixel';
import { useAIPersonalizeModal } from '@/contexts/AIPersonalizeModalContext';
import PackagingSelection from './PackagingSelection';
import AIPersonalizationFooter from './PersonalizeModalFooter';
import { GetDedicationImageURL, GetProcessedUrl, GetDedicationImage } from '@/services/api/personalize/DedicationImage';
import axios from 'axios';
import GlobalLoader from '../GlobalLoader';
import ConfirmationModal from './PersonalizeModal/ConfirmationModal';
import BookViewMenu from './PersonalizeModal/BookViewMenu';
import PersonalizeHeader from './PersonalizeModal/PersonalizeHeader';
import AIInfoStep from './AIInfoStep';
import AIReviewStep from './AIReviewStep';
import AIEditStep from './AIEditStep';
import AIPersonalizeModals from './AIPersonalizeModals';
import { useTranslation } from 'next-i18next';
import useUserPreferences from '@/hooks/useUserPreferences';
import { GenerateAIStory, PollAIStoryJob } from '@/services/api/personalize/GenerateAIStory';
import { usePriceCalculation } from '@/hooks/usePriceCalculation';
import { parseMarketScope } from '@/utils/parseMarketScope';
import { savePersonalization, loadPersonalizationArray, base64ToFile } from '@/utils/personalizationStorage';
import { saveAIStoryData, loadAIStoryData, mergeAIStoryWithDefault } from '@/utils/aiStoryStorage';

export default function AIPersonalizeModal({
  setPersonalizeModal,
  product,
  inputs,
  editFromCart,
}: {
  setPersonalizeModal: any;
  product: any;
  inputs: string[];
  editFromCart?: any;
}) {
  const router = useRouter();
  const { t } = useTranslation('personalizeModal');
  const { t: tAlerts } = useTranslation('alerts');
  const { locale } = router;
  const { productData, updateProductData, updateDedicationImage, setProductRtl } = useAIPersonalizeModal();
  const { user } = useAuth('none');
  const { setIsOpen, setProductType, setPostLoginCallback, setIsCallBack } = useLoginModal();
  const preferences = useUserPreferences();
  const cartDispatch = useCartDispatch();

  const { data: settings, isLoading: isSettingsLoading } = useQuery(
    ['settings', preferences?.countryCode, preferences?.currencyCode],
    () => GetSettings(preferences?.countryCode, preferences?.currencyCode)
  );

  // --- Stable IDs ---
  const is_edit = productData?.isEdit as string | undefined;

  const uuid = useMemo(() => {
    if (is_edit === '1') return product?.uuid || productData?.product?.uuid || uuidv4();
    return router.query.uuid ? (router.query.uuid as string) : uuidv4();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // stable on mount

  // --- Form state ---
  const [childName, setChildName] = useState<string>(() => {
    if (is_edit === '1') {
      return (
        productData?.product?.name ||
        product?.name ||
        (typeof product?.replace_name === 'string'
          ? product.replace_name
          : locale === 'ar' ? product?.replace_name?.ar : product?.replace_name?.en) ||
        ''
      );
    }
    return (router.query.name as string) || '';
  });

  const [inputAge, setInputAge] = useState<string>(() =>
    is_edit === '1'
      ? product?.inputs?.age || productData?.product?.age || ''
      : (router.query.age as string) || ''
  );

  const [childgender, setChildGender] = useState<string>(() =>
    is_edit === '1'
      ? productData?.product?.gender === 'Female' ? 'Female' : 'Male'
      : product?.has_male ? 'Male' : product?.has_female ? 'Female' : 'Male'
  );

  const [inputDedication, setInputDedication] = useState<string>(() =>
    is_edit === '1'
      ? product?.dedication || productData?.product?.dedication || ''
      : (router.query.dedication as string) || ''
  );

  const [inputCover, setInputCover] = useState<any>(() =>
    is_edit === '1'
      ? product?.cover_id || productData?.product?.cover_id
      : (router.query.cover as string) || undefined
  );

  const [inputPackaging, setInputPackaging] = useState<boolean | undefined>(() => {
    if (is_edit === '1') {
      if (product?.packaging?.name) return product.packaging.name === 'Packaging';
      if (typeof product?.packaging === 'boolean') return product.packaging;
      return false;
    }
    return false;
  });

  const [inputValues, setInputValues] = useState<Record<string, string>>({});
  const [isPostLoginFlow, setIsPostLoginFlow] = useState(false);

  // --- AI-specific state ---
  const [isAIGenerating, setIsAIGenerating] = useState(false);
  const [aiJobId, setAiJobId] = useState<string | null>(null);
  const [aiPollUrl, setAiPollUrl] = useState<string | null>(null);
  const [aiGeneratedData, setAiGeneratedData] = useState<any>(null);
  const [aiLoadingStep, setAiLoadingStep] = useState(0);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const aiJobCompletedRef = useRef(false);
  const [aiStoryImage, setAiStoryImage] = useState<any>(null);
  const [aiStoryFile, setAiStoryFile] = useState<any>(null);

  const [dedicationImageMode, setDedicationImageMode] = useState<'ai-image' | 'separate-image' | 'no-image'>(() => {
    if (is_edit === '1') {
      if (product?.use_ai_image_for_dedication === true) return 'ai-image';
      if (product?.dedication_image) return 'separate-image';
      if (productData?.use_ai_image_for_dedication === true) return 'ai-image';
      if (productData?.dedicationImage) return 'separate-image';
      return 'no-image';
    }
    return 'ai-image';
  });

  const aiLoadingSteps = [
    t('aiGeneratingStory'),
    t('aiMakingHero'),
    t('aiAddingCharacters'),
    t('aiFinalizingIllustrations'),
  ];

  // --- Image state ---
  const [croppedImage, setCroppedImage] = useState<string | null>(null);
  const [croppedFile, setCroppedFile] = useState<File | null>(null);
  const [loadData, setLoadData] = useState<any>(null);
  const [loadDataLoading, setLoadDataLoading] = useState(false);
  const [imageSelected, setImageSelected] = useState<string>(() => {
    if (is_edit === '1') {
      return (
        product?.use_ai_image_for_dedication === true ||
        product?.dedication_image ||
        productData?.use_ai_image_for_dedication === true ||
        productData?.dedicationImage
      ) ? 'yes' : 'no';
    }
    return 'yes';
  });
  const [imageInEdit, setImageInEdit] = useState<string | null>(null);

  // --- Navigation ---
  const [step, setStep] = useState<'info' | 'review' | 'edit'>(() => is_edit === '1' ? 'review' : 'info');
  const [activeOption, setActiveOption] = useState<string>('name');

  // --- Modal state ---
  const [dedicationModal, setDedicationModal] = useState(false);
  const [coverModal, setCoverModal] = useState(false);
  const [packageModal, setPackageModal] = useState(false);
  const [nameModal, setNameModal] = useState(false);
  const [confirmCart, setConfirmCart] = useState(false);

  // --- UI state ---
  const [userCurrency, setUserCurrency] = useState('ILS');
  const [priceSymbol, setPriceSymbol] = useState('₪');
  const [showPackagingOption, setShowPackagingOption] = useState(false);
  const [bookView, setBookview] = useState<'single' | 'double'>('single');
  const [bookViewMenu, setBookViewMenu] = useState(false);
  const [coverType, setCoverType] = useState('');
  const [option, setOption] = useState<number | undefined>(undefined);
  const [addToCartLoading, setAddToCartLoading] = useState(false);
  const [loadingProductEdit, setLoadingProductEdit] = useState(false);
  const desktopRef = useRef<any>();
  const [counter, setCounter] = useState(0);
  const [page, setPage] = useState(0);
  const [showSavedData, setShowSavedData] = useState(true);
  const [isProductRtlInBook, setIsProductRtlInBook] = useState(
    is_edit === '1'
      ? product?.is_rtl ?? productData?.isProductRtl ?? ''
      : product?.is_rtl ?? '0'
  );

  // --- Options ---
  const [options, setOptions] = useState(() => {
    const list = [
      { id: 'child', label: t('childLabel'), value: childName || '', completed: is_edit === '1' },
      { id: 'dedication', label: t('dedicationLabel'), value: inputDedication !== '' ? inputDedication : t('enterDedicationPlaceholderInputDedication'), completed: is_edit === '1' },
      { id: 'cover', label: t('coverLabel'), value: t('chooseCover'), completed: is_edit === '1' },
    ];
    if (is_edit === '1' && product?.packaging || showPackagingOption) {
      list.push({ id: 'packaging', label: t('packagingLabel'), value: (is_edit === '1' && product?.packaging) ? t('withPackaging') : t('withoutPackaging'), completed: is_edit === '1' });
    }
    return list;
  });

  const markOptionCompleted = (id: string, completed: boolean) => {
    setOptions((prev) => prev.map((opt) => (opt.id === id ? { ...opt, completed } : opt)));
  };

  // --- Stable callback refs ---
  const childNameRef = useRef(childName);
  const childGenderRef = useRef(childgender);
  useEffect(() => { childNameRef.current = childName; }, [childName]);
  useEffect(() => { childGenderRef.current = childgender; }, [childgender]);
  useEffect(() => { setImageInEdit(croppedImage); }, [croppedImage]);

  // --- Data queries ---
  const product_id = is_edit === '1' ? product?.product_id : product?.id;
  const { data: covers } = useQuery(['covers', product_id, userCurrency], GetCovers);

  // --- Computed values ---
  const imagePrice = (dedicationImageMode === 'separate-image' || dedicationImageMode === 'ai-image')
    ? settings?.image_price?.amount || 0
    : 0;

  const priceInPersonalize = usePriceCalculation({
    isEdit: is_edit ?? '0',
    product,
    covers,
    inputCover,
    inputPackaging,
    imagePrice,
    settings,
    showPackagingOption,
  });

  const customInputs = useMemo(() => ({
    name: childName || (is_edit === '1' ? productData?.product?.replace_name || '' : ''),
    first_name: '',
    age: inputAge || (is_edit === '1' ? productData?.product?.age || '' : ''),
    init: childName?.charAt(0) || '',
    dedication: inputDedication || (is_edit === '1' ? productData?.product?.dedication || '' : ''),
    gender: childgender,
  }), [childName, inputAge, inputDedication, productData, is_edit, childgender]);

  // --- Default book query ---
  const { data: defaultBookData, isLoading: isDefaultBookLoading } = useQuery(
    [
      'personalize',
      {
        slug: product?.slug || productData?.product?.product_slug || '',
        inputs: JSON.stringify(customInputs),
        gender: childgender,
        type: 2,
      },
    ],
    GetDocument,
    { enabled: step === 'review' }
  );

  // Use AI generated data if available, otherwise use default book data
  const bookData = useMemo(() => {
    if (is_edit === '1') {
      const savedAIData = loadAIStoryData(uuid);
      return mergeAIStoryWithDefault(savedAIData, defaultBookData) || defaultBookData;
    }
    return aiGeneratedData || defaultBookData;
  }, [aiGeneratedData, defaultBookData, is_edit, uuid]);

  const bookIsLoading = isAIGenerating && !aiGeneratedData;

  // --- Effects ---

  // Currency from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem('user-locale-choice');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.currency) {
          setUserCurrency(parsed.currency);
          const symbols: Record<string, string> = {
            ILS: '₪', USD: '$', EUR: '€', GBP: '£', CAD: '$',
            SAR: '﷼', AED: 'د.إ', OMR: '﷼', BHD: '.د.ب', QAR: 'ر.ق', KWD: 'د.ك',
          };
          setPriceSymbol(symbols[parsed.currency] || parsed.currency);
        }
      }
    } catch {}
  }, []);

  // Packaging availability
  useEffect(() => {
    if (is_edit === '1') return;
    try {
      const userCountryCode = preferences?.countryCode?.trim().toUpperCase();
      const rawScope = product?.market_scope ?? productData?.product?.market_scope;
      const scopeArray = parseMarketScope(rawScope);
      const hasIL = scopeArray.includes('IL');
      const isUserIL = userCountryCode === 'IL';
      const shouldShow = (scopeArray.length > 0 && hasIL && isUserIL) || scopeArray.length === 0;
      setShowPackagingOption(shouldShow);
      updateProductData({ showPackaging: shouldShow, gender: childgender });
      updateProductData({ marketScope: scopeArray });
    } catch {
      setShowPackagingOption(true);
    }
  }, [product?.market_scope, productData?.product?.market_scope, preferences?.countryCode, is_edit]);

  // Correct inputPackaging for non-IL users
  useEffect(() => {
    if (!showPackagingOption) setInputPackaging(undefined);
  }, [showPackagingOption]);

  // imageSelected cleanup in edit mode
  useEffect(() => {
    if (is_edit === '1' && imageSelected === 'no') {
      localStorage.removeItem(`dedicationImageUrl-${uuid}`);
    }
  }, [imageSelected, is_edit, uuid]);

  // Book page counter
  useEffect(() => {
    const id = setInterval(() => {
      setCounter((c) => c + 1);
      setPage(
        desktopRef?.current?.pageFlip()?.getPageCount() -
          desktopRef?.current?.pageFlip()?.getCurrentPageIndex()
      );
    }, 500);
    return () => clearInterval(id);
  }, []);

  // Reset isPostLoginFlow on step/unmount
  useEffect(() => { if (step === 'info') setIsPostLoginFlow(false); }, [step]);
  useEffect(() => () => { setIsPostLoginFlow(false); }, [is_edit]);

  // inputValues sync
  useEffect(() => {
    let urlInputs: Record<string, string> = {};
    if (router.query.inputs) {
      try { urlInputs = JSON.parse(router.query.inputs as string); } catch {}
    }
    const vals: Record<string, string> = {};
    Object.keys(inputs).forEach((key) => {
      vals[key] = urlInputs[key] || '';
    });
    if ('name' in vals) vals.name = childName || urlInputs.name || '';
    setInputValues(vals);
  }, [inputs, childName]);

  // Edit mode: set step + mark all completed
  useEffect(() => {
    if (is_edit !== '1') return;
    let urlInputs: Record<string, string> = {};
    if (router.query.inputs) {
      try { urlInputs = JSON.parse(router.query.inputs as string); } catch {}
    }
    const vals: Record<string, string> = {};
    Object.keys(inputs).forEach((key) => {
      const urlVal = urlInputs[key];
      vals[key] = (urlVal !== undefined && String(urlVal).trim() !== '') ? urlVal : (customInputs as any)[key] ?? '';
    });
    setInputValues(vals);
    markOptionCompleted('child', true);
    markOptionCompleted('gender', true);
    markOptionCompleted('name', true);
    markOptionCompleted('dedication', true);
    markOptionCompleted('cover', true);
    if (showPackagingOption) markOptionCompleted('packaging', true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [is_edit, showPackagingOption]);

  // Options sync
  useEffect(() => {
    const selectedCoverName = (() => {
      if (!covers || !inputCover) return '';
      const c = covers.find((x: any) => String(x.id) === String(inputCover));
      if (!c) return '';
      if (typeof c.name === 'string') return c.name;
      return locale === 'ar' ? (c.name?.ar || c.name?.en || '') : (c.name?.en || c.name?.ar || '');
    })();

    setOptions((prev) =>
      prev.map((opt) => {
        switch (opt.id) {
          case 'child': return { ...opt, value: childName };
          case 'dedication': return { ...opt, value: inputDedication !== '' ? inputDedication : t('enterDedicationPlaceholderInputDedication') };
          case 'cover': return { ...opt, value: selectedCoverName || t('chooseCover') };
          case 'packaging': return {
            ...opt,
            value: (is_edit === '1' && product?.packaging) ? t('withPackaging') : (inputPackaging === true ? t('withPackaging') : t('withoutPackaging')),
          };
          default: return opt;
        }
      })
    );
  }, [childName, inputDedication, inputCover, inputPackaging, covers, locale, t, is_edit, product?.packaging]);

  // Dedication {basmti} replace
  useEffect(() => {
    const replaceName = (is_edit === '1' && product?.replace_name !== undefined)
      ? product.replace_name
      : (childName || (locale === 'ar' ? product?.replace_name?.ar : product?.replace_name?.en));
    const newDedication = inputDedication.replace('{basmti}', replaceName);
    setInputDedication(newDedication);
    updateProductData({ dedication: newDedication });
    updateDedicationImage(croppedImage);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputDedication, childName]);

  // dedicationImageMode → productData sync
  useEffect(() => {
    updateProductData({
      dedicationImageMode,
      use_ai_image_for_dedication: dedicationImageMode === 'ai-image',
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dedicationImageMode]);

  // Sync AI story image to dedication storage
  const syncAIStoryImageToDedication = () => {
    if (dedicationImageMode !== 'ai-image') return;
    const key = localStorage.getItem(`aiStoryImage-${uuid}`);
    if (key) {
      localStorage.setItem(`dedicationImageUrl-${uuid}`, key);
      if (aiStoryImage && !croppedImage) setCroppedImage(aiStoryImage);
    }
  };

  useEffect(() => { syncAIStoryImageToDedication(); }, [dedicationImageMode, uuid, aiStoryImage]);
  useEffect(() => {
    if (aiStoryImage && dedicationImageMode === 'ai-image') syncAIStoryImageToDedication();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiStoryImage]);

  // Load saved AI story data in edit mode
  useEffect(() => {
    if (is_edit === '1') {
      const saved = loadAIStoryData(uuid);
      if (saved) setAiGeneratedData(saved);
    }
  }, [is_edit, uuid]);

  // Load dedication data in edit mode
  useEffect(() => {
    if (is_edit !== '1') return;
    const fetchDedicationData = async () => {
      setLoadingProductEdit(true);
      try {
        const savedAIData = loadAIStoryData(uuid);
        if (savedAIData) setAiGeneratedData(savedAIData);

        const aiStoryImageKey = localStorage.getItem(`aiStoryImage-${uuid}`);
        const storedProcessedKey = localStorage.getItem(`dedicationImageUrl-${uuid}`);

        if (product?.use_ai_image_for_dedication === true && aiStoryImageKey) {
          setDedicationImageMode('ai-image');
          try {
            const response = await GetDedicationImage(aiStoryImageKey);
            setAiStoryImage(response.data.url);
            localStorage.setItem(`dedicationImageUrl-${uuid}`, aiStoryImageKey);
            setCroppedImage(response.data.url);
          } catch {}
        } else if (storedProcessedKey) {
          setDedicationImageMode('separate-image');
          const response = await GetDedicationImage(storedProcessedKey);
          setCroppedImage(response.data.url);
        } else if (aiStoryImageKey) {
          setDedicationImageMode('ai-image');
          const response = await GetDedicationImage(aiStoryImageKey);
          setAiStoryImage(response.data.url);
          setCroppedImage(response.data.url);
          localStorage.setItem(`dedicationImageUrl-${uuid}`, aiStoryImageKey);
        } else {
          setDedicationImageMode('no-image');
        }

        setImageSelected(dedicationImageMode !== 'no-image' ? 'yes' : 'no');
      } catch {
      } finally {
        setLoadingProductEdit(false);
      }
    };
    fetchDedicationData();
  }, [is_edit, uuid, product]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, []);

  // --- AI story generation ---

  const pollAIStoryJob = async (pollUrl: string, timeoutRef?: { current: NodeJS.Timeout }) => {
    if (aiJobCompletedRef.current) return;
    try {
      const response = await PollAIStoryJob(pollUrl);

      if (response.completed_jobs && response.total_jobs) {
        const progress = response.completed_jobs / response.total_jobs;
        const stepIndex = Math.min(Math.floor(progress * aiLoadingSteps.length), aiLoadingSteps.length - 1);
        setAiLoadingStep(stepIndex);
      }

      if (response.status === 'completed') {
        aiJobCompletedRef.current = true;
        if (timeoutRef?.current) clearTimeout(timeoutRef.current);
        if (pollIntervalRef.current) { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; }
        setIsAIGenerating(false);
        setAiGeneratedData(response);
        saveAIStoryData(uuid, response);
        toast.custom((t) => <CustomToast type="success" t={t} message={tAlerts('ai_story_generated')} />);
      }

      if (response.status === 'failed') {
        aiJobCompletedRef.current = true;
        if (timeoutRef?.current) clearTimeout(timeoutRef.current);
        if (pollIntervalRef.current) { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; }
        setIsAIGenerating(false);
        setAiLoadingStep(0);
        toast.custom((t) => <CustomToast type="error" t={t} message={tAlerts('ai_story_generation_failed')} />);
      }
    } catch {}
  };

  const generateAIStoryMutation = useMutation(GenerateAIStory, {
    onSuccess: (data) => {
      if (!data?.poll_url || !data?.job_id) {
        setIsAIGenerating(false);
        toast.custom((t) => <CustomToast type="error" t={t} message={tAlerts('ai_story_generation_failed')} />);
        return;
      }

      setAiJobId(data.job_id);
      setAiPollUrl(data.poll_url);
      setAiLoadingStep(0);
      aiJobCompletedRef.current = false;

      const timeoutId = setTimeout(() => {
        if (pollIntervalRef.current) { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; }
        if (!aiJobCompletedRef.current) {
          aiJobCompletedRef.current = true;
          setIsAIGenerating(false);
          setAiLoadingStep(0);
          toast.custom((t) => <CustomToast type="error" t={t} message={tAlerts('ai_story_timeout')} />);
        }
      }, 120000);

      const timeoutRef = { current: timeoutId };

      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = setInterval(() => pollAIStoryJob(data.poll_url, timeoutRef), 3000);
    },
  });

  const handleGenerateAIStory = async () => {
    if (!aiStoryImage || !aiStoryFile) {
      toast.custom((tEl) => <CustomToast type="error" t={tEl} message={t('aiStoryImageRequired')} />);
      return;
    }

    setIsAIGenerating(true);
    setAiLoadingStep(0);
    let uploadedKey: string | undefined;

    try {
      try {
        const { data } = await GetDedicationImageURL(product?.slug || productData?.product?.product_slug);
        if (!data?.uploadUrl || !data?.uploadedKey) throw new Error('Invalid upload URL response');
        uploadedKey = data.uploadedKey;
        await axios.put(data.uploadUrl, aiStoryFile, {
          headers: { 'Content-Type': aiStoryFile.type || 'application/octet-stream' },
        });
      } catch {
        throw new Error('ai_story_image_upload_failed');
      }

      let processedImageUrl: string | undefined;
      try {
        if (!uploadedKey) throw new Error('uploaded key is undefined');
        const processedResponse = await GetProcessedUrl(uploadedKey);
        processedImageUrl = processedResponse?.data?.processedUrl;
        if (!processedImageUrl) throw new Error('No processed image URL');
      } catch {
        throw new Error('ai_story_image_processing_failed');
      }

      localStorage.setItem(`aiStoryImage-${uuid}`, uploadedKey!);

      try {
        generateAIStoryMutation.mutate({
          productSlug: product?.slug || productData?.product?.product_slug,
          payload: {
            inputs: JSON.stringify(customInputs),
            gender: childgender,
            child_image: processedImageUrl,
          },
        });
      } catch {
        throw new Error('ai_story_generation_failed');
      }
    } catch (error: any) {
      setIsAIGenerating(false);
      setAiLoadingStep(0);
      const errorMap: Record<string, string> = {
        ai_story_image_upload_failed: tAlerts('ai_story_image_upload_error'),
        ai_story_image_processing_failed: tAlerts('ai_story_image_processing_error'),
        ai_story_generation_failed: tAlerts('ai_story_generation_error'),
      };
      toast.custom((t) => (
        <CustomToast type="error" t={t} message={errorMap[error?.message] || tAlerts('ai_story_generation_error')} />
      ));
    }
  };

  // --- Handlers ---

  const handleNextStep = () => {
    if (!childName || childName === '') {
      toast.custom((tEl) => <CustomToast type="error" t={tEl} message={t('enterChildNameNotebook')} />, { duration: 2500 });
      return;
    }
    if (childgender === '') {
      toast.custom((tEl) => <CustomToast type="error" t={tEl} message={t('enterChildGenderError')} />, { duration: 2500 });
      return;
    }
    if (!aiStoryImage) {
      toast.custom((tEl) => <CustomToast type="error" t={tEl} message={t('aiStoryImageRequired')} />, { duration: 2500 });
      return;
    }
    markOptionCompleted('name', true);
    markOptionCompleted('gender', true);
    markOptionCompleted('child', true);
    setStep('review');
    handleGenerateAIStory();
  };

  const handleSave = () => {
    if (activeOption === 'dedication') {
      if (inputDedication.trim() === '') {
        toast.custom((tEl) => <CustomToast type="error" t={tEl} message={t('emptyDedicationError')} />, { duration: 2500 });
        return;
      }
      if (dedicationImageMode === 'ai-image') {
        if (!aiStoryImage) {
          toast.custom((tEl) => <CustomToast type="error" t={tEl} message={t('aiStoryImageRequiredForDedication')} />, { duration: 2500 });
          return;
        }
        updateDedicationImage(aiStoryImage);
        const aiKey = localStorage.getItem(`aiStoryImage-${uuid}`);
        if (aiKey) localStorage.setItem(`dedicationImageUrl-${uuid}`, aiKey);
      } else if (dedicationImageMode === 'separate-image') {
        if (!croppedImage) {
          toast.custom((tEl) => <CustomToast type="error" t={tEl} message={t('pictureSelectionError')} />, { duration: 2500 });
          return;
        }
        updateDedicationImage(croppedImage);
      } else {
        updateDedicationImage(null);
      }
      updateProductData({ dedication: inputDedication, dedicationImageMode });
      markOptionCompleted('dedication', true);
    }
    if (activeOption === 'cover') {
      if (!inputCover) {
        toast.custom((tEl) => <CustomToast type="error" t={tEl} message={t('coverSelectionError')} />, { duration: 2500 });
        return;
      }
      markOptionCompleted('cover', true);
    }
    if (activeOption === 'packaging' && showPackagingOption) {
      markOptionCompleted('packaging', true);
    }
    setStep('review');
  };

  const UpdateValue = (input: string, value: string) => {
    setInputValues((prev) => ({ ...prev, [input]: value }));
    if (input === 'name') { setChildName(value); updateProductData({ name: value }); }
    if (input === 'age') setInputAge(value);
  };

  const addToCart = async () => {
    setAddToCartLoading(true);
    setProductRtl(isProductRtlInBook);

    try {
      const selectedCover = covers?.find((x: any) => x.id == inputCover);

      let selectedPackagingPrice = 0;
      let packagingName = 'No-Packaging';
      if (showPackagingOption) {
        if (is_edit === '1' && product?.packaging?.name) {
          packagingName = product.packaging.name;
          selectedPackagingPrice = parseFloat(product.packaging.price || '0');
        } else if (inputPackaging === true) {
          packagingName = 'Packaging';
          selectedPackagingPrice = parseFloat(settings?.packaging_price || '0');
        }
      }

      const imagePriceForCart = (dedicationImageMode === 'separate-image' || dedicationImageMode === 'ai-image')
        ? settings?.image_price?.amount || 0
        : 0;

      const productBasePrice = is_edit === '1'
        ? Number(product?.price || product?.front_price?.amount || 0)
        : Number(product?.front_price?.amount || product?.price || 0);

      let selectedCoverPrice = 0;
      const selectedCoverInItem = covers?.find((x: any) => x.id == inputCover);
      if (selectedCoverInItem?.price?.amount !== undefined) {
        selectedCoverPrice = typeof selectedCoverInItem.price.amount === 'number'
          ? selectedCoverInItem.price.amount
          : parseFloat(selectedCoverInItem.price.amount) || 0;
      } else if (selectedCoverInItem?.price_usd !== undefined) {
        selectedCoverPrice = parseFloat(selectedCoverInItem.price_usd) || 0;
      } else if (selectedCoverInItem?.price_ils !== undefined) {
        selectedCoverPrice = parseFloat(selectedCoverInItem.price_ils) || 0;
      }

      const total_price = productBasePrice + selectedCoverPrice + selectedPackagingPrice + imagePriceForCart;

      const itemSlug = product?.slug ?? product?.product_slug ?? productData?.product?.product_slug;
      const itemImage = product?.images?.[0] ?? product?.image ?? product?.product?.image;

      let dedicationImageKey: string | null = null;
      let aiStoryImageKey: string | null = null;

      if (dedicationImageMode === 'ai-image') {
        aiStoryImageKey = localStorage.getItem(`aiStoryImage-${uuid}`);
        if (aiStoryImageKey) {
          dedicationImageKey = aiStoryImageKey;
          localStorage.setItem(`dedicationImageUrl-${uuid}`, aiStoryImageKey);
        } else if (aiStoryFile) {
          try {
            const { data } = await GetDedicationImageURL(itemSlug);
            if (data?.uploadUrl && data?.uploadedKey) {
              await axios.put(data.uploadUrl, aiStoryFile, { headers: { 'Content-Type': aiStoryFile.type || 'application/octet-stream' } });
              aiStoryImageKey = data.uploadedKey;
              dedicationImageKey = data.uploadedKey;
              localStorage.setItem(`aiStoryImage-${uuid}`, data.uploadedKey);
              localStorage.setItem(`dedicationImageUrl-${uuid}`, data.uploadedKey);
            }
          } catch {}
        } else if (aiStoryImage) {
          try {
            const file = await base64ToFile(aiStoryImage, 'ai-story-image.jpg', 'image/jpeg');
            if (file) {
              const { data } = await GetDedicationImageURL(itemSlug);
              if (data?.uploadUrl && data?.uploadedKey) {
                await axios.put(data.uploadUrl, file, { headers: { 'Content-Type': file.type || 'application/octet-stream' } });
                aiStoryImageKey = data.uploadedKey;
                dedicationImageKey = data.uploadedKey;
                localStorage.setItem(`aiStoryImage-${uuid}`, data.uploadedKey);
                localStorage.setItem(`dedicationImageUrl-${uuid}`, data.uploadedKey);
              }
            }
          } catch {}
        }
      } else if (dedicationImageMode === 'separate-image') {
        try {
          const { data } = await GetDedicationImageURL(itemSlug);
          if (data?.uploadUrl && data?.uploadedKey && croppedFile) {
            await axios.put(data.uploadUrl, croppedFile, { headers: { 'Content-Type': croppedFile.type || 'application/octet-stream' } });
            dedicationImageKey = data.uploadedKey;
            localStorage.setItem(`dedicationImageUrl-${uuid}`, data.uploadedKey);
          }
        } catch {}
      } else {
        const key = `dedicationImageUrl-${uuid}`;
        if (localStorage.getItem(key)) localStorage.removeItem(key);
      }

      if (!aiStoryImageKey) aiStoryImageKey = localStorage.getItem(`aiStoryImage-${uuid}`);
      if (aiGeneratedData) saveAIStoryData(uuid, aiGeneratedData);

      const packaging = showPackagingOption
        ? packagingName === 'Packaging'
          ? { name: 'Packaging', price: selectedPackagingPrice }
          : { name: 'No-Packaging', price: 0 }
        : undefined;

      const item = {
        product_id: product?.product_id || product?.id,
        uuid,
        replace_name: childName,
        inputs: { name: childName, age: inputAge },
        product_name: locale === 'ar' ? product?.product_name?.ar : product?.product_name?.en,
        demo_name: product?.demo_name ?? product?.product?.demo_name ?? productData?.product?.demo_name ?? '',
        cover_id: selectedCover?.id,
        default_replace_name: locale === 'ar' ? product?.replace_name?.ar : product?.replace_name?.en,
        image: itemImage,
        product_slug: itemSlug,
        price: productBasePrice,
        quantity: 1,
        total: total_price,
        product_type: 1 as const,
        discount: 0,
        gender: childgender,
        dedication_image: dedicationImageKey,
        dedication: inputDedication,
        packaging,
        ai_story_data: aiGeneratedData,
        is_ai: true,
        ai_story_image_key: aiStoryImageKey,
        dedication_image_mode: dedicationImageMode,
        use_ai_image_for_dedication: dedicationImageMode === 'ai-image',
      };

      if (is_edit === '1') {
        cartDispatch(updateItem(item));
        toast.custom((t) => <CustomToast type="success" t={t} message={tAlerts('cart_updated')} />);
      } else {
        cartDispatch(addItem(item));
        toast.custom((t) => <CustomToast type="success" t={t} message={tAlerts('cart_added')} />);
      }

      savePersonalization({
        childName,
        inputAge,
        childgender,
        inputDedication,
        inputCover,
        inputPackaging: showPackagingOption ? inputPackaging : undefined,
        imageSelected,
        croppedImageBase64: croppedImage ?? undefined,
        isFirstProductAdded: true,
      });

      event('add_to_cart', {
        currency: userCurrency,
        items: [{ item_id: item.product_id, item_name: String(item.product_name ?? ''), item_variant: selectedCover?.name ? String(selectedCover.name) : undefined, price: total_price, quantity: 1 }],
      });
      fbq.event('AddToCart', { content_name: String(item.product_name ?? ''), currency: userCurrency, value: total_price });

      router.push(`/products?name=${childName}`);
      setPersonalizeModal(false);
    } catch {
    } finally {
      setAddToCartLoading(false);
      setConfirmCart(false);
    }
  };

  // --- Saved data for InfoStep ---
  const savedData = loadPersonalizationArray();

  // --- JSX ---
  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-white z-[140] flex flex-col text-right h-full"
        id="ai-personalize-modal"
        key="modal"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 30 }}
        transition={{ duration: 0.5, ease: 'easeInOut' }}
      >
        <ConfirmationModal
          confirmCart={confirmCart}
          is_edit={is_edit}
          addToCart={addToCart}
          addToCartLoading={addToCartLoading}
          setConfirmCart={setConfirmCart}
          childName={childName}
        />

        {loadingProductEdit && <GlobalLoader heading={String(t('productLoadingText'))} />}
        {loadDataLoading && <GlobalLoader heading={String(t('dataImportLoadingText'))} />}
        {addToCartLoading && is_edit === '1' && <GlobalLoader heading={String(t('addToCartLoadingText'))} />}

        <BookViewMenu
          step={step}
          bookViewMenu={bookViewMenu}
          bookView={bookView}
          setBookview={setBookview}
          setBookViewMenu={setBookViewMenu}
        />

        <AIPersonalizeModals
          dedicationModal={dedicationModal}
          packageModal={packageModal}
          coverModal={coverModal}
          nameModal={nameModal}
          isSettingsLoading={isSettingsLoading}
          productData={productData}
          inputPackaging={inputPackaging}
          setInputPackaging={setInputPackaging}
          is_edit={is_edit}
          setPackageModal={setPackageModal}
          updateProductData={updateProductData}
          handleSave={handleSave}
          settings={settings}
          inputCover={inputCover}
          setInputCover={setInputCover}
          setCoverType={setCoverType}
          setCoverModal={setCoverModal}
          markOptionCompleted={markOptionCompleted}
          setInputDedication={setInputDedication}
          inputDedication={inputDedication}
          setOption={setOption}
          childgender={childgender}
          product={product}
          setDedicationModal={setDedicationModal}
          nameFromPersonalize={childName}
          croppedImage={croppedImage}
          setCroppedImage={setCroppedImage}
          imageSelected={imageSelected}
          setImageSelected={setImageSelected}
          updateDedicationImage={updateDedicationImage}
          croppedFile={croppedFile}
          setCroppedFile={setCroppedFile}
          imageInEdit={imageInEdit}
          inputValues={inputValues}
          UpdateValue={UpdateValue}
          setChildName={setChildName}
          setInputAge={setInputAge}
          inputs={inputs}
          setNameModal={setNameModal}
          childName={childName}
          inputAge={inputAge}
          showPackagingOption={showPackagingOption}
          userCurrency={userCurrency}
          isAIStory={true}
          aiStoryImage={aiStoryImage}
          setAiStoryImage={setAiStoryImage}
          setAiStoryFile={setAiStoryFile}
          dedicationImageMode={dedicationImageMode}
          setDedicationImageMode={setDedicationImageMode}
        />

        <PersonalizeHeader
          step={step}
          productData={productData}
          childName={childName}
          setPersonalizeModal={setPersonalizeModal}
          setStep={setStep}
          setActiveOption={setActiveOption}
          isAIStory={true}
        />

        <div className="flex-1 overflow-hidden h-full">
          {step === 'review' && (
            <AIReviewStep
              isLoading={bookIsLoading}
              data={bookData}
              inputDedication={inputDedication}
              product={product}
              productData={productData}
              isEdit={is_edit}
              childName={childName}
              name={childName}
              dedicationImage={croppedImage}
              imageSelected={imageSelected}
              bookView={bookView}
              isProductRtlInBook={isProductRtlInBook}
              desktopRef={desktopRef}
              setStep={setStep}
              setActiveOption={setActiveOption}
              setDedicationModal={setDedicationModal}
              isAIStory={true}
              isAIGenerating={isAIGenerating}
              aiLoadingStep={aiLoadingStep}
              dedicationImageMode={dedicationImageMode}
              aiStoryImage={aiStoryImage}
              croppedImage={croppedImage}
            />
          )}

          {(step === 'info' || step === 'edit') &&
            (activeOption === 'name' || activeOption === 'gender' ||
              activeOption === 'age' || activeOption === 'init' ||
              activeOption === 'first_name' || activeOption === 'child') && (
            <AIInfoStep
              inputValues={inputValues}
              inputs={inputs}
              is_edit={is_edit}
              UpdateValue={UpdateValue}
              setChildName={setChildName}
              setInputAge={setInputAge}
              step={step}
              updateProductData={updateProductData}
              handleNextStep={handleNextStep}
              setPersonalizeModal={setPersonalizeModal}
              childName={childName}
              inputAge={inputAge}
              childgender={childgender}
              savedData={savedData}
              setLoadData={setLoadData}
              loadDataLoading={loadDataLoading}
              user={user}
              preferences={preferences}
              showSavedData={showSavedData}
              setShowSavedData={setShowSavedData}
              isPostLoginFlow={isPostLoginFlow}
              isAIStory={true}
              croppedImage={aiStoryImage}
              setCroppedImage={setAiStoryImage}
              imageSelected={imageSelected}
              setImageSelected={setImageSelected}
              croppedFile={aiStoryFile}
              setCroppedFile={setAiStoryFile}
              aiStoryImage={aiStoryImage}
              aiStoryFile={aiStoryFile}
              dedicationImageMode={dedicationImageMode}
              setDedicationImageMode={setDedicationImageMode}
              setAiStoryImage={setAiStoryImage}
              setAiStoryFile={setAiStoryFile}
            />
          )}

          {step === 'edit' && activeOption === 'dedication' && (
            <AIEditStep
              childgender={childgender}
              product={product}
              setInputDedication={setInputDedication}
              inputDedication={inputDedication}
              setOption={setOption}
              nameFromPersonalize={childName}
              croppedImage={croppedImage}
              setCroppedImage={setCroppedImage}
              imageSelected={imageSelected}
              setImageSelected={setImageSelected}
              updateDedicationImage={updateDedicationImage}
              effectiveImageSelected={imageSelected}
              croppedFile={croppedFile}
              setCroppedFile={setCroppedFile}
              imageInEdit={imageInEdit}
              isAIStory={true}
              dedicationImageMode={dedicationImageMode}
              setDedicationImageMode={setDedicationImageMode}
              aiStoryImage={aiStoryImage}
            />
          )}

          {step === 'edit' && activeOption === 'cover' && (
            <div className="flex-1 overflow-y-auto p-4 bg-[#FFFCEB] min-h-full" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
              <CoversSelection
                product={productData?.product}
                inputCover={inputCover}
                setInputCover={setInputCover}
                setCoverType={setCoverType}
                isEdit={is_edit}
                userCurrency={userCurrency}
                updateProductData={updateProductData}
                handleSave={handleSave}
                markOptionCompleted={markOptionCompleted}
                setCoverModal={setCoverModal}
              />
            </div>
          )}

          {step === 'edit' && activeOption === 'packaging' && ((is_edit === '1' && product?.packaging) || showPackagingOption) && (
            <div className="flex-1 overflow-y-auto p-4 bg-[#FFFCEB] min-h-full" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
              {!isSettingsLoading ? (
                <PackagingSelection
                  product={productData?.product}
                  inputPackaging={inputPackaging}
                  setInputPackaging={setInputPackaging}
                  isEdit={is_edit}
                  updateProductData={updateProductData}
                  handleSave={handleSave}
                  markCompleted={markOptionCompleted}
                  packagingPrice={settings?.packaging_price}
                />
              ) : (
                <p>{t('packagingLoadingText')}</p>
              )}
            </div>
          )}
        </div>

        <AIPersonalizationFooter
          step={step}
          activeOption={activeOption}
          options={options}
          childName={childName}
          inputAge={inputAge}
          childgender={childgender}
          inputDedication={inputDedication}
          inputCover={inputCover}
          inputPackaging={inputPackaging}
          is_edit={is_edit}
          priceInPersonalize={priceInPersonalize}
          priceSymbol={priceSymbol}
          inputs={inputs}
          inputValues={inputValues}
          settings={settings}
          setStep={setStep}
          setActiveOption={setActiveOption}
          setNameModal={setNameModal}
          setDedicationModal={setDedicationModal}
          setCoverModal={setCoverModal}
          setPackageModal={setPackageModal}
          setConfirmCart={setConfirmCart}
          setPersonalizeModal={setPersonalizeModal}
          updateProductData={updateProductData}
          handleNextStep={handleNextStep}
          handleSave={handleSave}
          addToCart={addToCart}
          showPackagingOption={showPackagingOption}
        />
      </motion.div>
    </AnimatePresence>
  );
}
